/**
 * Cross-app auth event broadcasting via BroadcastChannel.
 *
 * Works when both apps share the same origin (same protocol + hostname + port).
 * If they're on different subdomains, switch to the shared-cookie approach instead.
 *
 * Usage:
 *   broadcastLogout()         — call inside signOutRedirect() when logging out
 *   listenForCrossAppLogout() — call once in AppRouter, returns cleanup fn
 */

const AUTH_CHANNEL = "auth_events";

type CustomTokenPayload = {
  access_token: string;
  refresh_token: string;
  id_token?: string;
  expires_in?: number;
  token_type?: string;
};

export type AuthBroadcastMessage =
  | { type: "LOGOUT"; timestamp: number; source: string }
  | { type: "SESSION_EXPIRED"; timestamp: number; source: string }
  | { type: "LOGIN"; timestamp: number; source: string; tokens?: CustomTokenPayload };

/**
 * Broadcast a logout event to all other tabs/apps on the same origin.
 */
export const broadcastLogout = (source = "datasurfr"): void => {
  try {
    const bc = new BroadcastChannel(AUTH_CHANNEL);
    const msg: AuthBroadcastMessage = { type: "LOGOUT", timestamp: Date.now(), source };
    console.log(`[AUTH][datasurfr][authBroadcast.ts] Broadcasting LOGOUT | source: ${source} | timestamp: ${msg.timestamp}`);
    bc.postMessage(msg);
    bc.close();
  } catch (err) {
    // BroadcastChannel not supported — fail silently
    console.warn("[AUTH][datasurfr][authBroadcast.ts] BroadcastChannel not available:", err);
  }
};

/**
 * Broadcast a login event (with tokens) to all other tabs on the same origin.
 */
export const broadcastLogin = (source = "datasurfr", tokens?: CustomTokenPayload): void => {
  try {
    const bc = new BroadcastChannel(AUTH_CHANNEL);
    const msg: AuthBroadcastMessage = { type: "LOGIN", timestamp: Date.now(), source, tokens };
    console.log(`[AUTH][datasurfr][authBroadcast.ts] Broadcasting LOGIN | source: ${source} | timestamp: ${msg.timestamp}`);
    bc.postMessage(msg);
    bc.close();
  } catch (err) {
    console.warn("[AUTH][datasurfr][authBroadcast.ts] BroadcastChannel not available:", err);
  }
};

/**
 * Listen for login events broadcast by other tabs on the same origin.
 * Returns a cleanup function — call it in useEffect cleanup.
 */
export const listenForCrossAppLogin = (
  onLogin: (source: string, tokens?: CustomTokenPayload) => void
): (() => void) => {
  try {
    const bc = new BroadcastChannel(AUTH_CHANNEL);
    bc.onmessage = (e: MessageEvent<AuthBroadcastMessage>) => {
      if (e.data?.type === "LOGIN") {
        console.log("[AUTH][datasurfr][authBroadcast.ts] Cross-app LOGIN received | source:", e.data.source, "| timestamp:", e.data.timestamp);
        onLogin(e.data.source, (e.data as { tokens?: CustomTokenPayload }).tokens);
      }
    };
    return () => {
      bc.close();
    };
  } catch (err) {
    console.warn("[AUTH][datasurfr][authBroadcast.ts] BroadcastChannel not available:", err);
    return () => {};
  }
};

/**
 * Listen for logout events broadcast by other tabs/apps.
 * Returns a cleanup function — call it in useEffect cleanup.
 */
export const listenForCrossAppLogout = (onLogout: (source: string) => void): (() => void) => {
  try {
    const bc = new BroadcastChannel(AUTH_CHANNEL);
    bc.onmessage = (e: MessageEvent<AuthBroadcastMessage>) => {
      if (e.data?.type === "LOGOUT") {
        console.warn("[AUTH][datasurfr][authBroadcast.ts] Cross-app LOGOUT received | source:", e.data.source, "| timestamp:", e.data.timestamp);
        bc.close();
        onLogout(e.data.source);
      }
    };
    return () => {
      bc.close();
    };
  } catch (err) {
    console.warn("[AUTH][datasurfr][authBroadcast.ts] BroadcastChannel not available:", err);
    return () => {};
  }
};
